/**
 * setTimeout / setInterval replacements whose *scheduling* happens on a Web
 * Worker thread.
 *
 * Why: browsers throttle timers on the main thread when a page is hidden.
 * This app runs as a cross-origin iframe, which is the worst case — Chrome's
 * intensive-throttling rules grant a leniency window only to frames that are
 * same-origin with the top-level window, so our chained timers can be
 * clamped to 1-minute aligned wakeups once the agent backgrounds the tab.
 * Worker timers are not subject to that clamp, so we use a worker purely as
 * a metronome. All real work (fetch, state updates) still runs on the main
 * thread; the worker only posts "your timer fired" messages.
 *
 * Scope of the fix — read before relying on this:
 *   - Fixes: timers firing late because the main thread's *timer task queue*
 *     is throttled. The worker's message lands as a posted-message task,
 *     which is not timer-throttled.
 *   - Does NOT fix: the frame's renderer process dying, the document being
 *     unloaded, or the React tree unmounting. If the frame is gone the worker
 *     goes with it. Those failures need detection outside this frame.
 *
 * Handles returned here live in their own namespace and are NOT interchangeable
 * with native timer ids. Always pair setWorkerTimeout with clearWorkerTimeout
 * (and the interval equivalents) — passing one of these handles to
 * window.clearTimeout would clear an unrelated timer.
 */

// The worker body, inlined so we don't need a webpack worker-loader entry or a
// separately deployed asset. It only tracks timer ids; no app logic here.
const WORKER_SCRIPT = `
  var timers = new Map();
  self.onmessage = function (e) {
    var data = e.data;
    var id = data.id;
    if (data.command === 'setInterval') {
      timers.set(id, setInterval(function () { self.postMessage({ id: id }); }, data.ms));
    } else if (data.command === 'setTimeout') {
      timers.set(id, setTimeout(function () {
        timers.delete(id);
        self.postMessage({ id: id });
      }, data.ms));
    } else if (data.command === 'clear') {
      var handle = timers.get(id);
      if (handle !== undefined) {
        clearTimeout(handle);
        clearInterval(handle);
        timers.delete(id);
      }
    }
  };
`;

type TimerKind = 'timeout' | 'interval';

interface TimerRecord {
  kind: TimerKind;
  ms: number;
  callback: () => void;
  /** Where this timer currently lives. Flips to false if the worker dies. */
  inWorker: boolean;
  /** Native timer id, only set when inWorker is false. */
  nativeId?: number;
}

/** Our own handle space, deliberately disjoint from native timer ids. */
let nextHandle = 1;
const records = new Map<number, TimerRecord>();

let worker: Worker | null = null;
/** Set false permanently once we learn workers are unusable in this context. */
let workerUsable = true;

/**
 * Schedule `record` on the main thread. Used both as the initial path when no
 * worker is available and as the recovery path when a worker dies.
 */
const armNatively = (handle: number, record: TimerRecord): void => {
  record.inWorker = false;
  if (record.kind === 'interval') {
    record.nativeId = window.setInterval(record.callback, record.ms);
  } else {
    record.nativeId = window.setTimeout(() => {
      records.delete(handle);
      record.callback();
    }, record.ms);
  }
};

/**
 * Abandon the worker and move every timer still living inside it back onto
 * native timers.
 *
 * Without this, a worker that dies mid-flight silently orphans its pending
 * timers — which is precisely the "polling stopped and nothing restarted it"
 * failure this module exists to prevent. Pending timeouts lose their elapsed
 * time and restart from their full delay; that is a deliberate trade for not
 * losing them entirely.
 */
const abandonWorker = (reason: string): void => {
  if (!workerUsable && worker === null) return;
  console.warn(`[workerTimer] ${reason}; moving timers to native timers`);
  workerUsable = false;
  try {
    worker?.terminate();
  } catch {
    // Terminating a already-dead worker is not actionable.
  }
  worker = null;
  records.forEach((record, handle) => {
    if (record.inWorker) armNatively(handle, record);
  });
};

const getWorker = (): Worker | null => {
  if (!workerUsable) return null;
  if (worker) return worker;

  try {
    const blob = new Blob([WORKER_SCRIPT], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const created = new Worker(url);
    // Safe to revoke immediately: the worker script fetch has already been
    // kicked off against the blob by this point.
    URL.revokeObjectURL(url);

    created.onmessage = (event: MessageEvent<{ id: number }>) => {
      const handle = event.data?.id;
      const record = handle == null ? undefined : records.get(handle);
      if (!record) return;
      // One-shot timers are done once they fire; the worker has already
      // dropped its own entry.
      if (record.kind === 'timeout') records.delete(handle);
      record.callback();
    };

    created.onerror = () => abandonWorker('worker reported an error');

    worker = created;
    return worker;
  } catch (e) {
    // No Worker constructor (jsdom), blocked by CSP (worker-src), or a
    // sandboxed frame without allow-same-origin. Degrade, don't throw.
    console.warn('[workerTimer] worker unavailable; using native timers:', e);
    workerUsable = false;
    return null;
  }
};

const schedule = (kind: TimerKind, callback: () => void, ms: number): number => {
  const handle = nextHandle++;
  const record: TimerRecord = { kind, ms, callback, inWorker: false };
  records.set(handle, record);

  const activeWorker = getWorker();
  if (activeWorker) {
    record.inWorker = true;
    activeWorker.postMessage({
      command: kind === 'interval' ? 'setInterval' : 'setTimeout',
      id: handle,
      ms,
    });
  } else {
    armNatively(handle, record);
  }
  return handle;
};

const clear = (handle: number | null | undefined): void => {
  if (handle == null) return;
  const record = records.get(handle);
  if (!record) return;
  records.delete(handle);

  if (record.inWorker) {
    worker?.postMessage({ command: 'clear', id: handle });
    return;
  }
  if (record.nativeId === undefined) return;
  if (record.kind === 'interval') {
    window.clearInterval(record.nativeId);
  } else {
    window.clearTimeout(record.nativeId);
  }
};

/** `setTimeout`, scheduled off the main thread so it survives throttling. */
export const setWorkerTimeout = (callback: () => void, ms: number): number =>
  schedule('timeout', callback, ms);

/** `setInterval`, scheduled off the main thread so it survives throttling. */
export const setWorkerInterval = (callback: () => void, ms: number): number =>
  schedule('interval', callback, ms);

/** Clear a handle from {@link setWorkerTimeout}. */
export const clearWorkerTimeout = (handle: number | null | undefined): void => clear(handle);

/** Clear a handle from {@link setWorkerInterval}. */
export const clearWorkerInterval = (handle: number | null | undefined): void => clear(handle);

/** True when scheduling is currently running on a worker. Diagnostics only. */
export const isUsingWorkerTimers = (): boolean => workerUsable && worker !== null;

/** Test seam: drop all state so each test starts from a clean module. */
export const __resetWorkerTimersForTests = (): void => {
  records.forEach((record, handle) => clear(handle));
  records.clear();
  try {
    worker?.terminate();
  } catch {
    // Nothing useful to do if the worker is already gone.
  }
  worker = null;
  workerUsable = true;
  nextHandle = 1;
};
